/**
 * GET /api/workshops — list endpoint
 * GET /api/workshops/{id} — detail endpoint (also returns myEnrollment + interestedUsers)
 *
 * Backend writes workshop with `id` (verified at backend/src/handlers/workshops.ts:327).
 * Detail endpoint returns `{ workshop: { ...workshop, sessions, slots }, myEnrollment?, interestedUsers? }`.
 *
 * Schema is intentionally permissive on optional fields: workshop content,
 * enrollment metadata, and session pricing are out-of-scope for Phase 3 ratchet.
 */
import { z } from 'zod';

export const TeamReservationSchema = z.looseObject({
  opcoId: z.string().nullable().optional(),
  opcoName: z.string().nullable().optional(),
  reservedBy: z.string().nullable().optional(),
  reservedAt: z.string().optional(),
});

export const WorkshopSlotSchema = z.looseObject({
  id: z.string().min(1).optional(),
  sessionId: z.string().nullable().optional(),
  type: z.string().optional(),
  status: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

// TASK-088 Phase 3: instructor assignment shape returned by getWorkshop.
// Key is `instructorAssignments` (renamed from `instructors` in Phase 3).
const InstructorAssignmentSchema = z.looseObject({
  userId: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  role: z.string().nullable().optional(),
  alt_host_status: z.string().nullable().optional(),
  alt_host_reason: z.string().nullable().optional(),
});

export const WorkshopSessionSchema = z.looseObject({
  id: z.string().min(1),
  name: z.string().nullable().optional(),
  startTime: z.string().nullable().optional(),
  endTime: z.string().nullable().optional(),
  capacity: z.number().nullable().optional(),
  enrolledCount: z.number().optional(),
  status: z.string().nullable().optional(),
  regionLabel: z.string().nullable().optional(),
  meetingUrl: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  slots: z.array(WorkshopSlotSchema).optional(),
  teamReservations: z.array(TeamReservationSchema).optional(),
  // TASK-090: viewer-OpCo-scoped roster of enrolled members for this session.
  enrolledMembers: z
    .array(
      z.looseObject({
        userId: z.string().nullable().optional(),
        email: z.string().nullable().optional(),
        firstName: z.string().nullable().optional(),
        lastName: z.string().nullable().optional(),
      }),
    )
    .optional(),
  // TASK-088 Phase 3: per-session instructor assignments (renamed from `instructors`).
  instructorAssignments: z.array(InstructorAssignmentSchema).optional(),
});

// TASK-088 Phase 3: instructor pool member shape returned by getWorkshop.
// Enriched with name + email from USER# record at read time.
const InstructorPoolMemberSchema = z.looseObject({
  userId: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  addedAt: z.string().nullable().optional(),
  addedBy: z.string().nullable().optional(),
  zoom_user_id: z.string().nullable().optional(),
  zoom_verified: z.boolean().optional(),
  zoom_verified_at: z.string().nullable().optional(),
  zoom_verify_reason: z.string().nullable().optional(),
});

export const WorkshopSchema = z.looseObject({
  id: z.string().min(1),
  title: z.string().optional(),
  description: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  signupRuleType: z.string().nullable().optional(),
  deliveryFormat: z.string().nullable().optional(),
  requiresApproval: z.boolean().optional(),
  startTime: z.string().nullable().optional(),
  endTime: z.string().nullable().optional(),
  capacity: z.number().nullable().optional(),
  enrolledCount: z.number().optional(),
  facilitator: z.string().nullable().optional(),
  materials: z.array(z.unknown()).optional(),
  sessions: z.array(WorkshopSessionSchema).optional(),
  slots: z.array(WorkshopSlotSchema).optional(),
  // TASK-088 Phase 3: instructor pool enriched with name + email.
  instructorPool: z.array(InstructorPoolMemberSchema).optional(),
  createdBy: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type Workshop = z.infer<typeof WorkshopSchema>;

/** GET /api/workshops — `{ workshops: Workshop[] }`. */
export const WorkshopsListResponseSchema = z.looseObject({
  workshops: z.array(WorkshopSchema),
});

export type WorkshopsListResponse = z.infer<typeof WorkshopsListResponseSchema>;

const EnrollmentSchema = z.looseObject({
  enrollmentId: z.string().optional(),
  workshopId: z.string().optional(),
  sessionId: z.string().nullable().optional(),
  userId: z.string().optional(),
  status: z.string().optional(),
  enrolledAt: z.string().optional(),
});

const InterestedUserSchema = z.looseObject({
  userId: z.string().min(1).optional(),
  sub: z.string().min(1).optional(),
  cognitoSub: z.string().min(1).optional(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.email().optional(),
  opcoId: z.string().nullable().optional(),
  opcoName: z.string().nullable().optional(),
  expressedInterestAt: z.string().optional(),
});

/**
 * GET /api/workshops/{id} — `{ workshop, myEnrollment?, interestedUsers? }`.
 * `myEnrollment` is null when the user has no record; `interestedUsers` is
 * present only for admin/leader roles.
 */
export const WorkshopDetailResponseSchema = z.looseObject({
  workshop: WorkshopSchema,
  myEnrollment: EnrollmentSchema.nullable().optional(),
  interestedUsers: z.array(InterestedUserSchema).optional(),
});

export type WorkshopDetailResponse = z.infer<typeof WorkshopDetailResponseSchema>;
